import { useState } from 'react'

import { ActionIcon, Group, Loader, Text } from '@mantine/core'
import { Dropzone, IMAGE_MIME_TYPE, PDF_MIME_TYPE } from '@mantine/dropzone'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { Redirect } from '@redwoodjs/router'
import { Metadata, useMutation, useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import DateInput from 'src/components/Inputs/DateInput/DateInput'
import MonthYearInput from 'src/components/Inputs/MonthYearInput/MonthYearInput'
import PhoneInput from 'src/components/Inputs/PhoneInput/PhoneInput'
import QuestionRow from 'src/components/Inputs/QuestionRow/QuestionRow'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_BASIC_COMPANY } from 'src/graphql/companies.graphql'
import { CREATE_VOE, CONFIRM_VOE_CONSENT_UPLOAD } from 'src/graphql/voe.graphql'
import IconClose from 'src/icons/IconClose'
import IconFile from 'src/icons/IconFile'
import IconUpload from 'src/icons/IconUpload'
import { formatBytes } from 'src/lib/formatters'
import { removeCountryCode, unformatPhoneNumber } from 'src/lib/phone.utils'
import { normalizeUuidPathParam } from 'src/lib/url.utils'

import PlacesAutocomplete from '../ApplicantDetailsPage/tabs/ApplicationTab/components/AddressInput/PlacesAutocomplete'
import PasswordInput from '../ApplicantDetailsPage/tabs/ApplicationTab/components/PasswordInput/PasswordInput'
import TextInput from '../ApplicantDetailsPage/tabs/ApplicationTab/components/TextInput/TextInput'
import Header from '../DriverApplicationPage/components/Header'
import ApplicationCompletedScreen from '../DriverApplicationPage/screens/ApplicationCompletedScreen'
import FieldLabel from '../SettingsPage/tabs/companyTab/components/FieldLabel'

const MEGABYTE = 1024 ** 2
const FIELD_LABEL_CLASS_NAME = 'text-sm font-medium text-doubleNickel-gray-700'

const GENERIC_VOE_SUBMISSION_ERROR =
  'Unable to create VoE request. Please try again later.'

// Redwood passes ValidationError messages through to the client but masks
// unexpected server errors, so surfacing the GraphQL message tells external
// requesters which field to fix instead of a dead-end generic failure.
export const getVoeSubmissionErrorMessage = (error?: {
  graphQLErrors?: ReadonlyArray<{ message?: string }>
}) => error?.graphQLErrors?.[0]?.message || GENERIC_VOE_SUBMISSION_ERROR

export const externalVoeRequestSchema = z
  .object({
    companyId: z.string(),
    companyName: z
      .string()
      .min(1, { message: 'This field is required' })
      .max(300, { message: '300 characters max' }),
    dotNumber: z
      .string()
      .refine(
        (data) => {
          if (!data || data.length === 0) return true
          return /^\d{2,50}$/.test(data)
        },
        {
          message: 'DoT Number must be between 2 and 50 digits',
        }
      )
      .optional()
      .or(z.literal('')),
    contactName: z
      .string()
      .min(1, { message: 'This field is required' })
      .max(300, { message: '300 characters max' }),
    email: z.string().email({ message: 'Invalid email' }),
    phoneNumber: z
      .string()
      .refine(
        (data) => unformatPhoneNumber(removeCountryCode(data)).length === 10,
        {
          message: 'Phone number should have 10 digits',
        }
      ),
    faxNumber: z
      .string()
      .refine(
        (data) =>
          unformatPhoneNumber(removeCountryCode(data)).length === 10 ||
          unformatPhoneNumber(removeCountryCode(data)).length === 0,
        {
          message: 'Fax number should have 10 digits',
        }
      )
      .optional()
      .nullable(),
    driverName: z.string(),
    // fullAddress: z.string(),
    ssn: z.string().length(9, { message: 'SSN should have 9 digits' }),
    dob: z.date(),
    positionHeld: z
      .string()
      .min(1, { message: 'This field is required' })
      .max(300, { message: '300 characters max' }),
    startDate: z
      .date({
        required_error: 'Start date is required',
      })
      .refine(
        (date) => {
          // Cannot be a future date
          const today = new Date()
          const currentMonth = new Date(
            today.getFullYear(),
            today.getMonth(),
            1
          )
          return date <= currentMonth
        },
        {
          message: 'Start date cannot be in the future',
        }
      ),
    endDate: z
      .date({
        required_error: 'End date is required',
      })
      .refine(
        (date) => {
          // Cannot be a future date
          const today = new Date()
          const currentMonth = new Date(
            today.getFullYear(),
            today.getMonth(),
            1
          )
          return date <= currentMonth
        },
        {
          message: 'End date cannot be in the future',
        }
      ),
    reasonForLeaving: z
      .string()
      .min(1, { message: 'This field is required' })
      .max(300, { message: '300 characters max' }),
    trailerTypes: z.string().max(500).optional().nullable(),
    isDriverTerminated: z.boolean(),
    isDriverSubjectToFMCSRs: z.boolean(),
    isJobDesignatedAsSafetySensitiveFunctionUnderDOT: z.boolean(),
    file: z.object({}).required(),
  })
  .refine(
    (data) => {
      // endDate cannot be less than startDate
      if (data.startDate && data.endDate && data.endDate < data.startDate) {
        return false
      }
      return true
    },
    {
      message: 'End date cannot be earlier than start date',
      path: ['endDate'], // This will show the error on the endDate field
    }
  )

export const buildExternalVoeInput = (values) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { file, ...voeInput } = values

  return {
    ...voeInput,
    companyName: values.companyName?.trim(),
    dotNumber: values.dotNumber?.trim() || null,
    contactName: values.contactName?.trim(),
    email: values.email?.trim().toLowerCase(),
    driverName: values.driverName?.trim(),
    ssn: values.ssn?.trim(),
    positionHeld: values.positionHeld?.trim(),
    reasonForLeaving: values.reasonForLeaving?.trim(),
    trailerTypes: values.trailerTypes?.trim(),
    phoneNumber: unformatPhoneNumber(values.phoneNumber),
    faxNumber: unformatPhoneNumber(values.faxNumber),
  }
}

const SelectedFile = ({ file, onClose }) => {
  if (!file) return null
  return (
    <div className="flex flex-row items-center gap-3">
      <IconFile className="h-10 w-8 fill-none" />
      <div className="items-center gap-1.5">
        <Text className="text-md font-medium text-doubleNickel-gray-900">
          {file.name}
        </Text>
        <Text className="text-sm font-medium text-doubleNickel-gray-500">
          {formatBytes(file.size)}
        </Text>
      </div>
      <ActionIcon
        variant="subtle"
        className="ml-auto h-6 w-6 rounded-md bg-doubleNickel-white"
        onClick={onClose}
      >
        <IconClose className="h-3 w-3 fill-doubleNickel-white stroke-doubleNickel-gray-500" />
      </ActionIcon>
    </div>
  )
}

const ExternalVoeRequestPage = ({ companyId: companyIdParam }) => {
  const companyId = normalizeUuidPathParam(companyIdParam)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [loadingSubmit, setLoadingSubmit] = useState(false)

  const { data: companyData, loading: loadingCompany } = useQuery(
    GET_BASIC_COMPANY,
    {
      variables: {
        id: companyId,
      },
      skip: !companyId,
    }
  )

  const companyName = companyData?.basicCompany?.companyName

  const [confirmConsentUpload] = useMutation(CONFIRM_VOE_CONSENT_UPLOAD)

  const [createVoe] = useMutation(CREATE_VOE, {
    onCompleted: async ({ createVerificationOfEmployment }) => {
      const { uploadUrl, voeId } = createVerificationOfEmployment
      const file = form.values.file as File

      console.info('[VoE Upload] Starting document upload', {
        voeId,
        fileName: file?.name,
        fileSize: file?.size,
        fileType: file?.type,
        uploadUrlPresent: !!uploadUrl,
        timestamp: new Date().toISOString(),
      })

      if (!file) {
        console.error('[VoE Upload] No file found in form values', { voeId })
        setLoadingSubmit(false)
        toast(
          'VoE request created but no document was attached. Please contact support.',
          'error'
        )
        return
      }

      if (!uploadUrl) {
        console.error('[VoE Upload] No upload URL received from server', {
          voeId,
        })
        setLoadingSubmit(false)
        toast(
          'VoE request created but upload URL missing. Please contact support.',
          'error'
        )
        return
      }

      try {
        console.info('[VoE Upload] Starting S3 PUT request', {
          voeId,
          fileName: file.name,
        })

        const startTime = Date.now()
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        })
        const uploadDuration = Date.now() - startTime

        console.info('[VoE Upload] S3 PUT request completed', {
          voeId,
          status: response.status,
          statusText: response.statusText,
          uploadDurationMs: uploadDuration,
          responseOk: response.ok,
        })

        if (!response.ok) {
          const errorText = await response
            .text()
            .catch(() => 'Unable to read error response')
          console.error('[VoE Upload] S3 upload failed', {
            voeId,
            status: response.status,
            statusText: response.statusText,
            errorResponse: errorText,
          })
          throw new Error(`Upload failed with status: ${response.status}`)
        }

        console.info('[VoE Upload] Document uploaded successfully', {
          voeId,
          fileName: file.name,
        })

        try {
          await confirmConsentUpload({ variables: { voeId } })
          console.info('[VoE Upload] Consent document record confirmed', {
            voeId,
          })
        } catch (confirmError) {
          console.error(
            '[VoE Upload] Failed to confirm consent upload record',
            {
              voeId,
              error:
                confirmError instanceof Error
                  ? confirmError.message
                  : 'Unknown error',
            }
          )
        }

        setLoadingSubmit(false)
        setFormSubmitted(true)
      } catch (error) {
        console.error('[VoE Upload] Exception during document upload', {
          voeId,
          fileName: file?.name,
          error: error instanceof Error ? error.message : 'Unknown error',
          errorStack: error instanceof Error ? error.stack : undefined,
          timestamp: new Date().toISOString(),
        })
        setLoadingSubmit(false)
        toast(
          'VoE request created but document upload failed. Please contact support.',
          'error'
        )
      }
    },
    onError: (error) => {
      console.error('[VoE Creation] GraphQL mutation failed', {
        error: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
        timestamp: new Date().toISOString(),
      })
      setLoadingSubmit(false)
      toast(getVoeSubmissionErrorMessage(error), 'error')
    },
  })

  const form = useForm({
    initialValues: {
      companyId: companyId,
      companyName: '',
      dotNumber: '',
      contactName: '',
      email: '',
      phoneNumber: '',
      faxNumber: '',
      driverName: '',
      companyAddress: '',
      ssn: '',
      dob: undefined,
      positionHeld: '',
      startDate: undefined,
      endDate: undefined,
      reasonForLeaving: '',
      trailerTypes: '',
      isDriverTerminated: false,
      isDriverSubjectToFMCSRs: true,
      isJobDesignatedAsSafetySensitiveFunctionUnderDOT: true,
      file: null,
    },
    validateInputOnBlur: true,
    validate: zodResolver(externalVoeRequestSchema),
  })

  // Show loading state while checking if company exists
  if (loadingCompany) {
    return (
      <>
        <Header />
        <div className="flex h-screen flex-col items-center justify-center">
          <Loader />
        </div>
      </>
    )
  }

  // Redirect to 404 if company is not found
  if (companyId && !companyData?.basicCompany) {
    return <Redirect to="/404" />
  }

  const onDrop = (files) => {
    form.setFieldValue('file', files[0])
  }

  const cleanSelectedFile = () => {
    form.setFieldValue('file', null)
  }

  const onFormSubmit = (values) => {
    console.info('[VoE Form] Starting form submission', {
      companyId: values.companyId,
      driverName: values.driverName,
      hasFile: !!values.file,
      fileName: values.file?.name,
      fileSize: values.file?.size,
      timestamp: new Date().toISOString(),
    })

    setLoadingSubmit(true)
    const cleanedInput = buildExternalVoeInput(values)

    console.info('[VoE Form] Input cleaned, creating VoE request', {
      driverNameBefore: values.driverName,
      driverNameAfter: cleanedInput.driverName,
      hasWhitespaceChanges: values.driverName !== cleanedInput.driverName,
    })

    createVoe({
      variables: {
        input: cleanedInput,
      },
    })
  }

  if (formSubmitted) {
    return (
      <>
        <Header />
        <div className="flex h-screen flex-col justify-center">
          <ApplicationCompletedScreen
            title="VoE Request Submitted!"
            subTitle={'Contact support with any questions'}
          />
        </div>
      </>
    )
  }

  return (
    <form
      onSubmit={form.onSubmit(
        (values) => onFormSubmit(values),
        () => {
          toast('Please correct the highlighted fields and try again.', 'error')
          // Wait a tick so Mantine renders the field errors before we look
          // them up. Mantine marks the errored InputWrapper with data-error.
          setTimeout(() => {
            const firstErrorField = document.querySelector(
              '[data-error], [aria-invalid="true"]'
            )
            firstErrorField?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            })
            const focusTarget =
              firstErrorField?.querySelector('input, textarea, select') ??
              firstErrorField
            if (focusTarget instanceof HTMLElement) {
              focusTarget.focus({ preventScroll: true })
            }
          }, 0)
        }
      )}
      className="flex h-[100dvh] flex-col justify-between gap-5"
    >
      <Metadata
        title="ExternalVoeRequest"
        description="ExternalVoeRequest page"
      />
      <Header companyId={companyId} />
      <div className="flex flex-1 flex-col gap-5 overflow-auto px-4 sm:px-24">
        {/* HEADER */}
        <div className="flex w-full flex-row">
          <div className="flex flex-1 flex-col">
            <div className="text-lg font-semibold text-doubleNickel-gray-900">
              {companyName} Employment Verification Request Form
            </div>
          </div>
        </div>

        {/* COMPANY INFO */}
        <div className="flex flex-col gap-6 rounded-lg border border-doubleNickel-gray-200">
          <div className="flex flex-col gap-6 p-6">
            <div className="text-md font-semibold text-doubleNickel-gray-900">
              Company Information
            </div>
            <div className="flex flex-col justify-between gap-4 sm:flex-row">
              <TextInput
                label="Company Name"
                required
                placeholder="Enter company name"
                {...form.getInputProps('companyName')}
                editing={true}
                className="flex-1"
              />
              <TextInput
                label={
                  <FieldLabel
                    label="DoT Number"
                    tooltip="Optional. Leave empty, or enter 2 to 50 digits."
                  />
                }
                value={form.values.dotNumber as string}
                error={form.errors.dotNumber as string}
                editing={true}
                className="flex-1"
                handleChange={(value) => {
                  const cleaned = value.replace(/\D/g, '')
                  form.setFieldValue('dotNumber', cleaned)
                }}
              />
              <TextInput
                label="Contact Name"
                required
                {...form.getInputProps('contactName')}
                editing={true}
                className="flex-1"
              />
            </div>
            <div className="flex flex-col justify-between gap-4 sm:flex-row">
              <TextInput
                label="Email"
                required
                {...form.getInputProps('email')}
                editing={true}
                className="flex-1"
              />
              <PhoneInput
                className="flex-1"
                required
                value={form.values.phoneNumber as string}
                label="Phone Number"
                classNames={{ label: FIELD_LABEL_CLASS_NAME }}
                error={form.errors['phoneNumber']}
                onChange={(value) => {
                  form.setFieldValue('phoneNumber', value)
                }}
              />
              <PhoneInput
                className="flex-1"
                value={form.values.faxNumber as string}
                label="Fax Number"
                classNames={{ label: FIELD_LABEL_CLASS_NAME }}
                error={form.errors['faxNumber']}
                onChange={(value) => {
                  form.setFieldValue('faxNumber', value)
                }}
              />
            </div>
            <PlacesAutocomplete
              label="Full Address"
              placeholder="Select an address from the dropdown"
              required
              value={form.values.companyAddress}
              handleChange={(value) => {
                form.setFieldValue(`companyAddress`, value)
              }}
            />
          </div>
        </div>

        {/* DRIVER INFO  */}
        <div className="flex flex-col gap-6 rounded-lg border border-doubleNickel-gray-200">
          <div className="flex flex-col gap-6 p-6">
            <div className="text-md font-semibold text-doubleNickel-gray-900">
              Driver Information
            </div>
            <div className="flex flex-col justify-between gap-4 sm:flex-row">
              <TextInput
                label="Driver Name"
                required
                {...form.getInputProps('driverName')}
                editing={true}
                className="flex-1"
              />
              <PasswordInput
                label="SSN"
                required
                value={form.values.ssn as string}
                handleChange={(value) => form.setFieldValue('ssn', value)}
                error={form.errors.ssn as string}
                editing={true}
                className="flex-1"
              />
              <DateInput
                label="Date of Birth"
                required
                className="flex-1"
                editing={true}
                {...form.getInputProps('dob')}
              />
            </div>
          </div>
        </div>

        {/* REQUEST CONTENT  */}
        <div className="flex flex-col gap-6 rounded-lg border border-doubleNickel-gray-200">
          <div className="flex flex-col gap-6 p-6">
            <div className="text-md font-semibold text-doubleNickel-gray-900">
              Request Form
            </div>
            <div className="flex flex-col justify-between gap-4 sm:flex-row">
              <TextInput
                label="Position Held"
                required
                {...form.getInputProps('positionHeld')}
                editing={true}
                className="flex-1"
              />

              <div className="flex flex-1 gap-4">
                <MonthYearInput
                  label="Employment Start Date"
                  required
                  className="w-full sm:w-48"
                  editing={true}
                  minDate={new Date('1900-01-01')}
                  maxDate={
                    form.values.endDate
                      ? new Date(form.values.endDate as Date)
                      : (() => {
                          const today = new Date()
                          return new Date(
                            today.getFullYear(),
                            today.getMonth(),
                            1
                          )
                        })()
                  }
                  {...form.getInputProps('startDate')}
                />
                <MonthYearInput
                  label="Employment End Date"
                  required
                  className="w-full sm:w-48"
                  editing={true}
                  minDate={
                    form.values.startDate
                      ? new Date(form.values.startDate as Date)
                      : new Date('1900-01-01')
                  }
                  maxDate={(() => {
                    const today = new Date()
                    return new Date(today.getFullYear(), today.getMonth(), 1)
                  })()}
                  {...form.getInputProps('endDate')}
                />
              </div>
            </div>
            <TextInput
              label="Reason for Leaving"
              required
              {...form.getInputProps('reasonForLeaving')}
              editing={true}
            />
            <TextInput
              label="Trailer Types"
              {...form.getInputProps('trailerTypes')}
              editing={true}
            />
            <QuestionRow
              text="Was the driver terminated?"
              required
              value={form.values.isDriverTerminated as boolean}
              handleChange={(value) =>
                form.setFieldValue(`isDriverTerminated`, value === 'true')
              }
              editing={true}
            />
            <QuestionRow
              text="Was the driver subject to FMCSRs while employed?"
              required
              value={form.values.isDriverSubjectToFMCSRs as boolean}
              handleChange={(value) =>
                form.setFieldValue(`isDriverSubjectToFMCSRs`, value === 'true')
              }
              editing={true}
            />
            <QuestionRow
              text="Was the driver's job designated as a safety-sensitive function in a DoT regulated mode subject to Drug and Alcohol testing per 49 CFR Part 40?"
              required
              value={
                form.values
                  .isJobDesignatedAsSafetySensitiveFunctionUnderDOT as boolean
              }
              handleChange={(value) =>
                form.setFieldValue(
                  `isJobDesignatedAsSafetySensitiveFunctionUnderDOT`,
                  value === 'true'
                )
              }
              editing={true}
            />
          </div>
        </div>

        <div className="flex flex-col gap-6 rounded-lg border border-doubleNickel-gray-200">
          <div className="flex flex-col gap-6 p-6">
            <div className="text-md font-semibold text-doubleNickel-gray-900">
              Upload driver consent document{' '}
              <span className="text-red-500">*</span>
            </div>
            <div className="flex flex-col justify-between gap-4 sm:flex-row">
              {!form.values.file && (
                <div className="w-full flex-row py-1">
                  <Dropzone
                    accept={[...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE]}
                    maxFiles={1}
                    maxSize={5 * MEGABYTE}
                    onDrop={onDrop}
                    {...form.getInputProps('file')}
                  >
                    <Group className="flex flex-col">
                      <Dropzone.Accept>
                        <p>Drag and drop the file here</p>
                      </Dropzone.Accept>
                      <Dropzone.Reject>
                        <p>This file type is not supported</p>
                      </Dropzone.Reject>
                      <Dropzone.Idle>
                        <ActionIcon
                          variant="subtle"
                          className="h-10 w-10 rounded-lg border border-doubleNickel-gray-200 bg-doubleNickel-white shadow-sm"
                        >
                          <IconUpload className="fill-none stroke-doubleNickel-gray-700" />
                        </ActionIcon>
                      </Dropzone.Idle>
                      <div className="flex flex-col items-center gap-1">
                        <Text className="text-sm text-doubleNickel-gray-600">
                          <span className="font-semibold text-doubleNickel-brand-500">
                            {'Click to upload '}
                          </span>
                          or drag and drop
                        </Text>
                        <Text
                          size="sm"
                          className="text-xs text-doubleNickel-gray-600"
                        >
                          PDF, PNG, JPG or JPEG (Max 5MB)
                        </Text>
                      </div>
                    </Group>
                  </Dropzone>
                </div>
              )}

              {form.values.file && (
                <>
                  <SelectedFile
                    file={form.values.file}
                    onClose={cleanSelectedFile}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mb-10 flex w-full flex-row items-center justify-end">
          {!loadingSubmit && (
            <Button
              // disabled={!form.isValid()}
              text="Submit"
              className="w-full sm:w-32"
              type="submit"
            />
          )}
          {loadingSubmit && <Loader />}
        </div>
      </div>
    </form>
  )
}

export default ExternalVoeRequestPage
